import { BackArrow, SaveIcon } from "allIcons";
import {
  BodyContainer,
  TextEditor,
  HeaderContainer,
  IconWrapper,
  InputField,
  PreviewCard,
  PrimaryHeading,
  RightMainContainer,
  SecondaryHeading,
  AddComponent,
  SelectField,
  MainButton,
  PopOver,
  DropDownCard,
  Loader,
  DeleteButton,
} from "commonUI";
import { useParams, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { getUserSavePermission, isEmpty, parse, useUtilities } from "helpers";
import { useTags } from "api/siteSettings/Tags";
import {
  useAddContentRequestItem,
  useContentRequestsItem,
  useDeleteContentRequestItem,
  useUpdateContentRequestItem,
} from "api/templateLibrary/contentRequestsItem";
import { useUserPermissions } from "hooks/user";
import { t } from "i18next";
import { ModulePrefixes } from "enums";

export default function AddEditContentRequestItem() {
  const { search } = useLocation();
  const { data: contentRequestsItem, isLoading: loadingContentRequestItem } =
    useContentRequestsItem();
  const { mutate: deleteContentRequestItem, isLoading: isDeleting } =
    useDeleteContentRequestItem(true);
  const {
    user: { userData },
    notification,
    navigate,
  } = useUtilities();
  const { mutate: addContent, isLoading: isAdding } =
    useAddContentRequestItem();
  const { mutate: updateContent, isLoading: isUpdating } =
    useUpdateContentRequestItem();
  const { id } = useParams();
  const [openAddTagModal, setOpenAddTagModal] = useState(false);
  const { data: tagList } = useTags();
  const { add_new_templates, edit_templates, delete_templates } =
    useUserPermissions();

  const [inputValues, setInputValues] = useState({
    title: "",
    hint: "Text",
    instructions: "",
    tags: [],
  });

  useEffect(() => {
    if (
      id === "add" ||
      loadingContentRequestItem ||
      contentRequestsItem.length === 0
    )
      return;
    const item = contentRequestsItem.filter(
      (_) => String(_.id) === String(id)
    )[0];
    if (isEmpty(item)) return;
    setInputValues({
      title: item.title,
      hint: item.hint || item.type,
      instructions: item.instructions || item.description,
      tags: parse(item.tags),
    });
  }, [id, contentRequestsItem, loadingContentRequestItem]);

  function hanldeSave() {
    if (inputValues.title === "") {
      notification({
        type: "error",
        message: t("common.input.error.titleRequired"),
      });
      return;
    }

    if (!inputValues.hint) {
      notification({
        type: "error",
        message: t("libraryTemplate.contentRequestItems.input.error.selectContentType"),
      });
      return;
    }

    if (id === "add" && add_new_templates) {
      addContent({
        ...inputValues,
        requested_from: {
          user_avatar: userData.user_avatar,
          role: userData.rolesData?.[0]?.role_title ?? "Client",
          name: userData?.name,
          group: "Admin",
          id: userData?.id,
        },
      });
      return;
    }
    if (edit_templates)
      updateContent({
        ...inputValues,
        id,
      });
  }

  return (
    <>
      <RightMainContainer>
        {isAdding || isUpdating || isDeleting ? <Loader loader="block" /> : ""}
        <HeaderContainer>
          <IconWrapper>
            <BackArrow
              onClick={() =>
                navigate(
                  search.split("=")[1] ??
                    `/${ModulePrefixes.TEMPLATE_LIBRARY}/content-request-items`
                )
              }
            />
            <PrimaryHeading>
              {id === "add" ? t("libraryTemplate.contentRequestItems.addContentRequestItem") : inputValues.title}
            </PrimaryHeading>
          </IconWrapper>
          <div className="flex items-center justify-center gap-14">
            <DeleteButton
              show={id !== "add" && delete_templates}
              onDelete={() => deleteContentRequestItem(id)}
              deleteMessage={t("libraryTemplate.delete.message")}
              deleteTitle={t("libraryTemplate.delete.title")}
              deleteButtonText={t("libraryTemplate.delete.button")}
            />
            {getUserSavePermission({
              id,
              addPermission: add_new_templates,
              editPermission: edit_templates,
            }) ? (
              <MainButton onClick={hanldeSave}>
                <SaveIcon />
                {t("button.saveChanges")}
              </MainButton>
            ) : (
              ""
            )}
          </div>
        </HeaderContainer>
        <BodyContainer>
          <InputField
            label={t("common.input.title.label")}
            placeholder={t("common.input.title.placeholder")}
            value={inputValues.title}
            setValue={(value) =>
              setInputValues((pre) => ({ ...pre, title: value }))
            }
          />
          <TextEditor
            label={t("libraryTemplate.contentRequestItems.input.instructions.label")}
            placeholder={t("libraryTemplate.contentRequestItems.input.instructions.placeholder")}
            editorClass="mt-2"
            option={false}
            rows={5}
            value={inputValues.instructions}
            setValue={(value) =>
              setInputValues((pre) => ({ ...pre, instructions: value }))
            }
          />
          <div className="mt-3">
            <SecondaryHeading>{t("libraryTemplate.contentRequestItems.input.contentType.label")}</SecondaryHeading>
            <SelectField
              value={inputValues.hint}
              setValue={(value) =>
                setInputValues((pre) => ({ ...pre, hint: value }))
              }
            >
              <option value="Text">{t("libraryTemplate.contentRequestItems.input.contentType.text")}</option>
              <option value="Image">{t("libraryTemplate.contentRequestItems.input.contentType.image")}</option>
              <option value="Video">{t("libraryTemplate.contentRequestItems.input.contentType.video")}</option>
              <option value="Link">{t("libraryTemplate.contentRequestItems.input.contentType.link")}</option>
              <option value="File">{t("libraryTemplate.contentRequestItems.input.contentType.file")}</option>
            </SelectField>
          </div>
          <div className="mt-2">
            <SecondaryHeading>{t("common.input.tags.label")}</SecondaryHeading>
            <div className="flex items-center gap-5 flex-wrap">
              {inputValues.tags.map((ele) => (
                <PreviewCard
                  icon
                  color={ele.color}
                  title={ele.text}
                  className="m-0"
                  onCross={() =>
                    setInputValues((pre) => ({
                      ...pre,
                      tags: pre.tags.filter(
                        (_) => String(_.id) !== String(ele.id)
                      ),
                    }))
                  }
                />
              ))}
            </div>
          </div>
          <AddComponent
            value={openAddTagModal}
            onClick={() => setOpenAddTagModal(true)}
            title={t("common.input.tags.addTag")}
          >
            <PopOver
              open={openAddTagModal}
              onClose={() => setOpenAddTagModal(false)}
              toolTip={{ vertical: "bottom", horizontal: "center" }}
              style={{
                top: "unset",
                left: "unset",
                right: "unset",
                bottom: "calc(100% + 15px)",
              }}
            >
              <DropDownCard
                title={t("common.input.tags.label")}
                selectedValues={inputValues.tags.map((ele) => ele.id)}
                listStyle
                filterSelectedItems
                list={tagList?.map((ele) => ({
                  ...ele,
                  index: ele.id,
                  text: ele.tag_title,
                }))}
                onSelect={(value) =>
                  setInputValues((pre) => ({
                    ...pre,
                    tags: [
                      ...pre.tags,
                      {
                        id: value.id,
                        text: value.tag_title,
                        color: value.color,
                      },
                    ],
                  }))
                }
              />
            </PopOver>
          </AddComponent>
        </BodyContainer>
      </RightMainContainer>
    </>
  );
}
